import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Divider } from '@mui/material';

interface SubscriptionCancelDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    subscriptionData?: {
        product: {
            name: string;
            totalCharge: number;
            billingCycle?: string;
        };
        nextBillingAt: string;
    };
}

const SubscriptionCancelDialog: React.FC<SubscriptionCancelDialogProps> = ({
    open,
    onClose,
    onConfirm,
    subscriptionData,
}) => {
    const handleConfirm = () => {
        onConfirm();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    p: 1,
                },
            }}
        >
            <DialogTitle
                sx={{
                    fontSize: '24px',
                    fontWeight: 700,
                    color: '#111827',
                    textAlign: 'center',
                    pb: 2,
                }}
            >
                구독 취소
            </DialogTitle>

            <DialogContent sx={{ px: 4, py: 2 }}>
                <Box sx={{ mb: 3 }}>
                    <Typography
                        sx={{
                            fontSize: '18px',
                            fontWeight: 600,
                            color: '#111827',
                            mb: 2,
                        }}
                    >
                        현재 구독
                    </Typography>

                    {subscriptionData && (
                        <Box
                            sx={{
                                backgroundColor: '#F9FAFB',
                                borderRadius: 2,
                                p: 3,
                                mb: 3,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    color: '#111827',
                                    mb: 1,
                                }}
                            >
                                {subscriptionData.product.name} 플랜
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: '20px',
                                    fontWeight: 700,
                                    color: '#111827',
                                    mb: 2,
                                }}
                            >
                                {subscriptionData.product.billingCycle === 'yearly' ? '1년마다' : '1개월마다'}{' '}
                                {subscriptionData.product.totalCharge.toLocaleString()}원
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    color: '#6B7280',
                                    lineHeight: 1.6,
                                }}
                            >
                                구독이 취소되지만 청구 기간이 종료되는{' '}
                                {new Date(subscriptionData.nextBillingAt).toLocaleDateString('ko-KR', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                })}
                                까지 사용할 수 있습니다.
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    color: '#6B7280',
                                    lineHeight: 1.6,
                                    mt: 1,
                                }}
                            >
                                마음이 바뀌면 구독을 갱신할 수 있습니다.
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <Divider />

            <DialogActions
                sx={{
                    flexDirection: 'column',
                    gap: 2,
                    p: 4,
                    pt: 3,
                }}
            >
                <Button
                    variant="contained"
                    fullWidth
                    onClick={handleConfirm}
                    sx={{
                        backgroundColor: '#374151',
                        color: 'white',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '16px',
                        py: 1.5,
                        borderRadius: 2,
                        '&:hover': {
                            backgroundColor: '#1F2937',
                        },
                    }}
                >
                    구독 취소
                </Button>
                <Button
                    variant="outlined"
                    fullWidth
                    onClick={onClose}
                    sx={{
                        backgroundColor: 'white',
                        color: '#374151',
                        borderColor: '#D1D5DB',
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '16px',
                        py: 1.5,
                        borderRadius: 2,
                        '&:hover': {
                            backgroundColor: '#F9FAFB',
                            borderColor: '#9CA3AF',
                        },
                    }}
                >
                    뒤로 돌아가기
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SubscriptionCancelDialog;
